/**
 * ListContext - Context for managing List state and operations
 * 
 * Lists are single-day collections of places, unlike Itineraries which have multiple days.
 * Lists use sequenceIndex for ordering (no dayIndex).
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import type { ListSlot, ListPost } from '@/models/listTypes';
import { WebAnalytics } from '@/lib/analytics';
import {
  EVENT_CREATE_LIST_LOCATION_ADDED,
  EVENT_CREATE_LIST_LOCATION_REMOVED
} from '@/lib/analyticsEvents';
import { generateTempListSlotId, extractListSlotIdentifier } from '@/utils/idGeneration';
import { stopIdsMatch } from '@/utils/postLocationStopIdentity';

/** Where to insert a new list slot. Default matches the Add modal (top of list). */
export interface AddSlotOptions {
  position?: 'start' | 'end';
}

/** Exported for `useListOptional` return typing. */
export interface ListContextValue {
  slots: ListSlot[];
  setSlots: React.Dispatch<React.SetStateAction<ListSlot[]>>;
  tripTitle: string;
  setTripTitle: (value: unknown) => void;
  listNotes: string;
  setListNotes: (notes: string) => void;
  listPost: ListPost | null;
  setListPost: (post: ListPost | null) => void;
  isEditMode: boolean;
  setIsEditMode: (isEdit: boolean) => void;
  editingPostId: string | null;
  setEditingPostId: (id: string | null) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  isPrivate: boolean;
  setIsPrivate: (isPrivate: boolean) => void;
  remixPostObj: Record<string, unknown> | null;
  setRemixPostObj: (obj: Record<string, unknown> | null) => void;
  addSlot: (slot: ListSlot, options?: AddSlotOptions) => void;
  removeSlot: (slotId: string) => void;
  updateSlot: (slotId: string, updates: Partial<ListSlot>) => void;
  reorderSlots: (newOrder: ListSlot[]) => void;
}

const ListContext = createContext<ListContextValue | undefined>(undefined);

export const useList = () => {
  const context = useContext(ListContext);
  if (!context) {
    throw new Error('useList must be used within a ListProvider');
  }
  return context;
};

/** Same store as `useList` but returns undefined outside `ListProvider` (e.g. combined place modal on other routes). */
export const useListOptional = (): ListContextValue | undefined => useContext(ListContext);

export const ListProvider = ({ children }: { children: ReactNode }) => {
  const [slots, setSlots] = useState<ListSlot[]>([]);
  const [tripTitle, setTripTitleState] = useState<string>('');
  const [listNotes, setListNotes] = useState<string>('');
  const [listPost, setListPost] = useState<ListPost | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPrivate, setIsPrivate] = useState<boolean>(false);
  const [remixPostObj, setRemixPostObj] = useState<Record<string, unknown> | null>(null);

  // Normalize trip title input
  const normalizeTripTitle = useCallback((value: unknown): string => {
    if (typeof value === 'string') {
      return value;
    }

    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'object') {
      const valueWithName = value as { name?: string; title?: string };
      if (typeof valueWithName.name === 'string') {
        return valueWithName.name;
      }
      if (typeof valueWithName.title === 'string') {
        return valueWithName.title;
      }

      if (Array.isArray(value)) {
        const firstString = value.find((item) => typeof item === 'string');
        if (typeof firstString === 'string') {
          return firstString;
        }
      }
    }

    try {
      return String(value);
    } catch {
      return '';
    }
  }, []);

  const setTripTitle = useCallback(
    (value: unknown) => {
      const normalized = normalizeTripTitle(value);
      setTripTitleState(normalized);
    },
    [normalizeTripTitle]
  );

  // Add a slot to the list
  // position: 'end' appends at bottom without renumbering existing slots — all add flows
  // (Add modal, inline search, custom place) now append so the cover photo (first slot) is stable.
  // position: 'start' (legacy default) prepends at top with sequenceIndex = 0, others incremented.
  const addSlot = useCallback(
    (slot: ListSlot, options?: AddSlotOptions) => {
      const position = options?.position ?? 'start';
      setSlots((prev) => {
        const existingId = extractListSlotIdentifier(slot);
        const newSlotId = generateTempListSlotId(existingId || undefined);

        const trackAdded = (newSlot: ListSlot) => {
          WebAnalytics.trackContentCreation(EVENT_CREATE_LIST_LOCATION_ADDED, {
            list_id: listPost?._id || editingPostId,
            list_name: tripTitle,
            place_id: newSlot.placeId || newSlot.locationId,
            place_name: newSlot.name,
            is_user_created: !!newSlot.isUserCreated,
            surface: 'list',
          });
        };

        if (position === 'end') {
          const maxSequenceIndex =
            prev.length > 0
              ? Math.max(
                  ...prev.map((s) =>
                    typeof s.sequenceIndex === 'number' ? s.sequenceIndex : 0
                  )
                )
              : -1;
          const newSlot: ListSlot = {
            ...slot,
            sequenceIndex: maxSequenceIndex + 1,
            _id: newSlotId,
            _clientId: newSlotId,
          };
          trackAdded(newSlot);
          return [...prev, newSlot];
        }

        const updatedSlots = prev.map((s) => {
          const currentSequenceIndex = typeof s.sequenceIndex === 'number' ? s.sequenceIndex : 0;
          return {
            ...s,
            sequenceIndex: currentSequenceIndex + 1,
          };
        });

        const newSlot: ListSlot = {
          ...slot,
          sequenceIndex: 0,
          _id: newSlotId,
          _clientId: newSlotId,
        };

        trackAdded(newSlot);
        return [newSlot, ...updatedSlots];
      });
    },
    [listPost?._id, editingPostId, tripTitle]
  );

  // Remove a slot from the list
  // Matches Itinerary deletion behavior: removes item but doesn't reorder sequenceIndex
  const removeSlot = useCallback((slotId: string) => {
    setSlots((prev) => {
      const removedSlot = prev.find((slot) => stopIdsMatch(slot, slotId));
      const filtered = prev.filter((slot) => !stopIdsMatch(slot, slotId));

      // Track location removed event (matches mobile's "Create List: Location Removed")
      if (removedSlot) {
        WebAnalytics.trackContentCreation(EVENT_CREATE_LIST_LOCATION_REMOVED, {
          list_id: listPost?._id || editingPostId,
          list_name: tripTitle,
          place_id: removedSlot.placeId || removedSlot.locationId,
          place_name: removedSlot.name,
          is_user_created: !!removedSlot.isUserCreated,
          surface: 'list',
        });
      }

      // Don't reorder sequenceIndex (matches Itinerary behavior)
      return filtered;
    });
  }, [listPost?._id, editingPostId, tripTitle]);

  // Update a slot in the list
  // Handles time updates (maps 'time' to 'timeRange' like ItineraryContext)
  const updateSlot = useCallback(
    (slotId: string, updates: Partial<ListSlot & { time?: string }>) => {
      setSlots((prev) =>
        prev.map((slot) => {
          if (stopIdsMatch(slot, slotId)) {
            // Handle time updates: PlaceCard sends 'time', but we store as 'timeRange'
            // This matches how ItineraryContext handles time updates
            const normalizedUpdates: Partial<ListSlot> = { ...updates };
            if (updates?.time !== undefined || updates?.timeRange !== undefined) {
              // Prefer timeRange if both are provided, otherwise use whichever is provided
              normalizedUpdates.timeRange = updates.timeRange || updates.time;
              // Remove 'time' from updates since we store as 'timeRange'
              delete (normalizedUpdates as { time?: string }).time;
            }
            return { ...slot, ...normalizedUpdates };
          }
          return slot;
        })
      );
    },
    []
  );

  // Reorder slots in the list
  const reorderSlots = useCallback((newOrder: ListSlot[]) => {
    // Update sequenceIndex based on new order
    const reordered = newOrder.map((slot, index) => ({
      ...slot,
      sequenceIndex: index,
    }));
    setSlots(reordered);
  }, []);

  const value: ListContextValue = {
    slots,
    setSlots,
    tripTitle,
    setTripTitle,
    listNotes,
    setListNotes,
    listPost,
    setListPost,
    isEditMode,
    setIsEditMode,
    editingPostId,
    setEditingPostId,
    isLoading,
    setIsLoading,
    isPrivate,
    setIsPrivate,
    remixPostObj,
    setRemixPostObj,
    addSlot,
    removeSlot,
    updateSlot,
    reorderSlots,
  };

  return <ListContext.Provider value={value}>{children}</ListContext.Provider>;
};

